import React, { useEffect, useState, useMemo } from "react";
import Chart from "react-apexcharts";
import jsonData from "../../../../current-survey-numeric.json"; // Ensure the path is correct

const TopCenterChart = ({ filterCriteria }) => {
  const [filteredScores, setFilteredScores] = useState({
    NR: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  const [unfilteredScores, setUnfilteredScores] = useState({
    NR: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  const [showMessage, setShowMessage] = useState(false); // State to control message visibility

  useEffect(() => {
    const processScores = (data) => {
      const scores = {
        NR: 0,
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
      };

      data.forEach((item) => {
        const score = item.MQ0008; // Use MQ0008 field for scoring
        if ([1, 2, 3, 4, 5].includes(score)) {
          scores[score]++;
        } else {
          scores.NR++;
        }
      });

      return scores;
    };

    // Filter the data based on the provided filterCriteria
    const filteredData = jsonData.filter((item) => {
      return Object.keys(filterCriteria).every(
        (key) =>
          filterCriteria[key].length === 0 ||
          filterCriteria[key].includes(item[key])
      );
    });

    console.log("Filter Criteria:", filterCriteria);
    console.log("Filtered Data:", filteredData);

    // Show message if there are fewer than 4 respondents
    setShowMessage(filteredData.length < 4);

    // Process the data if there are enough respondents
    if (filteredData.length >= 4) {
      const newFilteredScores = processScores(filteredData);
      const newUnfilteredScores = processScores(jsonData);

      setFilteredScores(newFilteredScores);
      setUnfilteredScores(newUnfilteredScores);

      console.log("Filtered Scores:", newFilteredScores);
      console.log("Unfiltered Scores:", newUnfilteredScores);
    }
  }, [filterCriteria]);

  const labelToScoreKeyMap = {
    "Intensely Dislike": 1,
    "Dislike": 2,
    "Neither Like nor Dislike": 3,
    "Like my Job": 4,
    "Love my Job": 5,
    "NR": "NR",
  };

  // Calculate totals for percentages
  const totalFiltered = Object.values(filteredScores).reduce(
    (acc, curr) => acc + curr,
    0
  );
  const totalUnfiltered = Object.values(unfilteredScores).reduce(
    (acc, curr) => acc + curr,
    0
  );

  const seriesData = Object.keys(filteredScores).map((key) => {
    const filteredPercentage =
      totalFiltered > 0 ? (filteredScores[key] / totalFiltered) * 100 : 0;
    const unfilteredPercentage =
      totalUnfiltered > 0 ? (unfilteredScores[key] / totalUnfiltered) * 100 : 0;

    return {
      x: key,
      y: filteredPercentage,
      goals: [
        {
          name: "Unfiltered",
          value: unfilteredPercentage,
          strokeHeight: 12,
          strokeColor: "#6D6D6D",
        },
      ],
    };
  });

  const options = useMemo(
    () => ({
      chart: {
        type: "bar",
        height: "300px",
      },
      colors: [
        "#F28E2B",
        "#F28E2B",
        "#F1CE63",
        "#499894",
        "#499894",
        "#BAB0AC",
      ],
      plotOptions: {
        bar: {
          borderRadius: 4,
          borderRadiusApplication: "end",
          horizontal: true,
          distributed: true,
          dataLabels: {
            position: "top",
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function (val, opt) {
          const xAxisLabel = opt.w.globals.labels[opt.dataPointIndex];
          const scoreKey = labelToScoreKeyMap[xAxisLabel];
          const filteredValue = filteredScores[scoreKey] || 0;
          const unfilteredValue = unfilteredScores[scoreKey] || 0;

          const filteredPercentage =
            totalFiltered > 0
              ? ((filteredValue / totalFiltered) * 100).toFixed(1)
              : "0.0";
          const unfilteredPercentage =
            totalUnfiltered > 0
              ? ((unfilteredValue / totalUnfiltered) * 100).toFixed(1)
              : "0.0";

          return `${filteredPercentage}% | ${unfilteredPercentage}%`;
        },
        style: {
          colors: ["#000"],
        },
        offsetX: 65,
      },
      xaxis: {
        categories: [
          "Intensely Dislike",
          "Dislike",
          "Neither Like nor Dislike",
          "Like my Job",
          "Love my Job",
          "NR",
        ],
        title: {
          text: "% of Responses",
          style: {
            fontSize: "12px",
            fontWeight: "normal",
            color: "#000",
          },
        },
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const xAxisLabel = w.globals.labels[dataPointIndex];
          const scoreKey = labelToScoreKeyMap[xAxisLabel];
          const filteredValue = filteredScores[scoreKey] || 0;
          const unfilteredValue = unfilteredScores[scoreKey] || 0;

          const filteredPercentage =
            totalFiltered > 0
              ? ((filteredValue / totalFiltered) * 100).toFixed(1)
              : "0.0";
          const unfilteredPercentage =
            totalUnfiltered > 0
              ? ((unfilteredValue / totalUnfiltered) * 100).toFixed(1)
              : "0.0";

          return `<div class="apexcharts-tooltip-text">
                    <strong>${xAxisLabel}</strong>: ${filteredPercentage}% | ${unfilteredPercentage}%
                  </div>`;
        },
      },
      yaxis: {
        max: 100,
        labels: {
          formatter: (val) =>
            typeof val === "number" ? `${val.toFixed(0)}` : val,
        },
      },
      legend: {
        show: false,
      },
    }),
    [filteredScores, unfilteredScores, totalFiltered, totalUnfiltered]
  );

  return (
    <div>
      {showMessage && (
        <div style={{ color: "red", marginTop: "20px" }}>
          Current filter selections are returning fewer than the minimum number
          of respondents. Please expand the selection.
        </div>
      )}
      {!showMessage && (
        <div id="d08-top-ctr">
          <Chart
            key={JSON.stringify(seriesData)} // Ensure uniqueness
            options={options}
            series={[{ name: "Filtered", data: seriesData }]}
            type="bar"
            height="300px"
          />
        </div>
      )}
    </div>
  );
};

export default TopCenterChart;
